import {
  EventPage,
  EventPreRegistrationPage,
  EventSaveTheDatePage,
  type LatestEventPagePathQuery,
} from "./types";

export type { QueryResult } from "../../typed-query";
import { typedQuery } from "../../typed-query";
import {
  videoAssetFragment,
  eventFragment,
  contentBlocksFragment,
} from "../fragments/events";

/**
 * @returns the chapters for the event page for the given slug and series
 * if the event series is "built-for-you"
 */
const bfyChaptersFragment = `
chaptersTitle,
chapters[]{
  _type,
  _key,
  title,
  videoAsset->{
    ${videoAssetFragment}
  },
  productVisual{
    medias{
      mobile{
        type,
        image {
          "src": asset->url,
          "alt": asset->altText,
          "width": asset->metadata.dimensions.width,
          "height": asset->metadata.dimensions.height
        },
        rive
      },
      desktop{
        type,
        image {
          "src": asset->url,
          "alt": asset->altText,
          "width": asset->metadata.dimensions.width,
          "height": asset->metadata.dimensions.height
        },
        rive
      }
    },
    valueProps[]{
      title,
      description
    }
  },
  ctaBlock{
    label,
    title,
    description,
    button
  }
}`;

/**
 * @returns the slugs of all event pages for the given series
 */
export const allEventPagesSlugsQuery = typedQuery<Pick<EventPage, "slug">[]>(`
*[_type == "eventPage" && defined(eventReference->series) && eventReference->series == $series]{
  "slug": path.current
}`);

/**
 * @returns the path of the latest or "Closest in-time" event page for the given series
 */
export const latestEventPagePathQuery = typedQuery<LatestEventPagePathQuery>(`*[
  _type == "eventPage" &&
  defined(eventReference->series) &&
  eventReference->series == $series
] | order(eventReference->timings.start desc)[0]{
  "path": path.current,
}`);

/**
 * @returns the event page for the given slug and series
 */
export const eventPageQuery = typedQuery<EventPage>(`
*[_type == "eventPage" && path.current == $slug && defined(eventReference->series) && eventReference->series == $series][0]{
  ${eventFragment},
  ${contentBlocksFragment},

  $series == "built-for-you" => {
    ${bfyChaptersFragment}
  }
}`);

/**
 * @returns the metadata for the event page for the given slug and series
 */
export const eventPageMetadataQuery = typedQuery<
  Pick<EventPage, "seo" | "eventReference" | "slug">
>(`
*[_type == "eventPage" && path.current == $slug && defined(eventReference->series) && eventReference->series == $series][0]{
  "slug": path.current,
  seo,
  eventReference->{
    title
  }
}`);

export const eventPreRegistrationPageQuery =
  typedQuery<EventPreRegistrationPage>(`
*[
    _type == "eventPage" &&
    defined(eventReference->series) &&
    eventReference->series == $series &&
    path.current == $slug &&
    defined(preRegistrationPage)
][0]{
  _id,
  _type,
  eventReference->{
    ${eventFragment}
  },
  preRegistrationPage->{
    _id,
    _type,
    navigation{
      links[]{
        label,
        href
      }
    },
    metaData{
      metaTitle,
      metaDescription,
      "ogImageUrl": ogImage.asset->url
    },

    $series == "pioneer" => {
      title,
      description,
      formModalTriggerLabel,
      formModalTitle,
      backdropImages[]{
        "url": asset->url,
        "height": asset->metadata.dimensions.height,
        "width": asset->metadata.dimensions.width
      }
    }
  },
}`);

export const eventSaveTheDatePageQuery = typedQuery<EventSaveTheDatePage>(`
*[
    _type == "eventPage" &&
    defined(eventReference->series) &&
    eventReference->series == $series &&
    path.current == $slug &&
    defined(saveTheDatePage)
][0]{
  _id,
  _type,
  eventReference->{
    ${eventFragment}
  },
  saveTheDatePage->{
    _id,
    _type,
    navigation{
      links[]{
        label,
        href
      }
    },
    metaData{
      metaTitle,
      metaDescription,
      "ogImageUrl": ogImage.asset->url
    },

    $series == "pioneer" => {
      title,
      description,
      formModalTriggerLabel,
      formModalTitle,
      backdropImages[]{
        "url": asset->url,
        "height": asset->metadata.dimensions.height,
        "width": asset->metadata.dimensions.width
      }
    }
  },
}`);
